import React, { useState, useEffect } from "react";
import { MSelectCard } from "@/components/ui/atoms/MSelectCard";
import { useNavigate } from "react-router-dom";
import { getMemberInfo, useProfileUpdate } from "@/hooks/useMembers";
import MHeader from "@/components/ui/organisms/MHeader";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import { useMedicines } from "@/hooks/useMedicine";
import { useProfileField } from "@/hooks/useHealthProfile";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { MInputField } from "@/components/ui/atoms/MInputField";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { MedicalDetailType } from "@/types/health-profile";
import MSpacer from "@/components/ui/atoms/MSpacer";
import styles from "./allergies.module.css";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from "@/constants/analytics.constants";

const AddAllergy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const { showToast } = useToast();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAddAllergy, setShowAddAllergy] = useState(false);
  const [customAllergy, setCustomAllergy] = useState("");
  const [saving, setSaving] = useState(false);
  const { updateMemberInLocalStorage } = useProfileUpdate();
  const [stagedAllergies, setStagedAllergies] = useState<
    { type: "Food" | "Medicine" | "Other"; name: string }[]
  >([]);
  const [initialAllergies, setInitialAllergies] = useState<
    { type: "Food" | "Medicine" | "Other"; name: string }[]
  >([]);

  const { searchResults, searchMedicines, searchLoading } = useMedicines();
  const { fieldValue: existingAllergies = [], saveField: saveAllergies } =
    useProfileField<{ type: "Food" | "Medicine" | "Other"; name: string }[]>(
      memberInfo.id,
      MedicalDetailType.ALLERGIES,
      []
    );

  // Initialize stagedAllergies when component mounts or existingAllergies changes
  useEffect(() => {
    if (Array.isArray(existingAllergies)) {
      setStagedAllergies([...existingAllergies]);
      setInitialAllergies([...existingAllergies]);
    }
  }, [existingAllergies]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.ALLERGIES_ADD,
    });
  }, []);

  const {
    values: typeAheadOptions,
    loading: typeAheadLoading,
    fetch: fetchTypeAhead,
    setCategory: setTypeAheadCategory,
    add,
  } = useTypeAhead();

  const handleSave = async (allergies: any[]) => {
    // Check if allergies array contains full objects with type and name
    const isFullArray = allergies.length > 0 && allergies[0].type;
    
    // If it's a full array (from appendCustom), use it directly; otherwise format it
    const formatted = isFullArray ? allergies : handleSelect(allergies);
    
    // Check if there are any changes
    const hasChanges =
      formatted.length !== initialAllergies.length ||
      !formatted.every((val) =>
        initialAllergies.some(
          (init) => init.type === val.type && init.name === val.name
        )
      );

    if (!hasChanges) {
      setDrawerOpen(false); // Just close the drawer if no changes
      return;
    }

    setSaving(true);
    try {
      // Remove duplicates (by type+name)
      const uniqueAllergies = formatted.filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.type === item.type &&
              t.name.toLowerCase() === item.name.toLowerCase()
          )
      );
      await saveAllergies(uniqueAllergies);
      updateMemberInLocalStorage(memberInfo.id, memberInfo.onBehalf);
      navigate("/profile/health-profile/allergies");
    } catch (error) {
      console.error("Error saving allergies:", error);
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const selectAllergyType = async (type: string) => {
    setSelectedType(type);
    
    // Track allergy type selection
    let screenName = ANALYTICS_SCREEN_NAMES.ALLERGIES_ADD_OTHERS;
    if (type === "food") {
      screenName = ANALYTICS_SCREEN_NAMES.ALLERGIES_ADD_FOOD;
    } else if (type === "medicine") {
      screenName = ANALYTICS_SCREEN_NAMES.ALLERGIES_ADD_MEDICINE;
    }
    
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName,
    });
    
    let typeAheadPrefix;
    switch (type) {
      case "food":
        typeAheadPrefix = "food_allergies_";
        break;
      case "medicine":
        typeAheadPrefix = "drug_allergies_";
        break;
      case "other":
        typeAheadPrefix = "any_other_allergies_";
        break;
    }
    const cat = `${typeAheadPrefix}${memberInfo.memberType}`;
    setTypeAheadCategory(cat);
    await fetchTypeAhead();
    setDrawerOpen(true);
  };

  const selectedAllergyType = (): "Food" | "Medicine" | "Other" => {
    if (selectedType === "food") return "Food";
    if (selectedType === "medicine") return "Medicine";
    return "Other";
  };

  const getSelectedAllergies = () => {
    const filterType = selectedAllergyType();
    return stagedAllergies
      .filter((a) => a.type === filterType)
      .map((a) => a.name);
  };

  const handleSelect = (values: string[]) => {
    // Only update the staged allergies for this type
    const typeLabel = selectedAllergyType();
    const otherTypes = stagedAllergies.filter((a) => a.type !== typeLabel);
    const newTypeAllergies = (Array.isArray(values) ? values : [values]).map(
      (v: string) => ({ type: typeLabel, name: v })
    );
    const updatedAllergies = [...otherTypes, ...newTypeAllergies];
    setStagedAllergies(updatedAllergies);
    return updatedAllergies;
  };

  const appendCustom = (custom: string) => {
    const typeLabel = selectedAllergyType();
    // Get all allergies except the current type
    const otherTypes = stagedAllergies.filter((a) => a.type !== typeLabel);
    // Get current type allergies and add the custom one
    const currentTypeAllergies = stagedAllergies
      .filter((a) => a.type === typeLabel)
      .map((a) => a.name);
    currentTypeAllergies.push(custom);
    // Return full array with all types
    const allAllergies = [
      ...otherTypes,
      ...currentTypeAllergies.map((name) => ({ type: typeLabel, name })),
    ];
    return allAllergies;
  };

  const checkAlreadyExists = (name: string) => {
    const typeLabel = selectedAllergyType();
    const valueToCheck = name.trim().toLowerCase();
    return stagedAllergies.some(
      (a) => a.type === typeLabel && a.name.toLowerCase() === valueToCheck
    );
  };

  return (
    <div className="profile-page-root">
      <MHeader module="addallergies" memberInfo={memberInfo} />
      <MSpacer />
      <div className={styles.allergy_type_container}>
        <MSelectCard
          text={t("allergies.food_allergy")}
          selected={selectedType === "food"}
          onClick={() => selectAllergyType("food")}
          className="w-full"
          centerAlign
        />
        <MSelectCard
          text={t("allergies.medicine_allergy")}
          selected={selectedType === "medicine"}
          onClick={() => selectAllergyType("medicine")}
          className="w-full"
          centerAlign
        />
        <MSelectCard
          text={t("allergies.other_allergy")}
          selected={selectedType === "other"}
          onClick={() => selectAllergyType("other")}
          className="w-full"
          centerAlign
        />
      </div>

      {/* MDropDownDrawer for Food and Any other allergy */}
      {(selectedType === "food" || selectedType === "other") && drawerOpen && (
        <MDropDownDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t("allergies.select_allergy")}
          items={typeAheadOptions.map((opt) => ({ value: opt, label: opt }))}
          variant="multi"
          selectedValues={getSelectedAllergies()}
          onSelect={handleSelect}
          onDone={handleSave}
          doneText={t("common.done")}
          doneLoading={saving}
          onOtherOptionClick={(selectedAllergies) => {
            handleSelect(selectedAllergies);
            setDrawerOpen(false);
            setShowAddAllergy(true);
          }}
        />
      )}

      {/* Add Allergy Drawer */}
      {selectedType === "medicine" && drawerOpen ? (
        <FooterDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t("allergies.add_drug_allergy")}
          showClose
          height="73vh"
          onDone={() => handleSave(appendCustom((customAllergy || '').trim()))}
          doneText={t("common.done")}
          doneDisabled={
            saving ||
            !(customAllergy || '').trim() ||
            checkAlreadyExists(customAllergy || '')
          }
          donePosition="top"
        >
          <div className={styles.allergy_medicine_container}>
            <label className="profile-input-label">
              {t("allergies.medicine_name")}
            </label>
            <AutoCompleteInput
              data={searchResults}
              getOptionLabel={(med) =>
                med.dosage
                  ? `${med.medicine_name} · ${med.dosage}`
                  : med.medicine_name
              }
              renderOption={(med) => (
                <div>
                  <div className="font-medium text-gray-900">
                    {med.medicine_name}
                  </div>
                  {med.dosage && (
                    <div className="text-xs text-gray-500">{med.dosage}</div>
                  )}
                </div>
              )}
              onSelect={(item) => setCustomAllergy(item.medicine_name)}
              placeholder={t("allergies.search_medicine")}
              value={customAllergy}
              onChange={(v) => setCustomAllergy(v)}
              loading={searchLoading}
              onSearch={searchMedicines}
              className="w-full"
            />
          </div>
        </FooterDrawer>
      ) : (
        <FooterDrawer
          open={showAddAllergy}
          onClose={() => setShowAddAllergy(false)}
          title={t("allergies.add_allergy")}
          showClose
          height="73vh"
          onDone={() => {
            handleSave(appendCustom((customAllergy || '').trim()));
          }}
          doneText={t("common.done")}
          doneDisabled={
            saving || !(customAllergy || '').trim() || checkAlreadyExists(customAllergy || '')
          }
          donePosition="top"
        >
          <div className={styles.allergy_medicine_container}>
            <MInputField
              name="custom-allergy-input"
              label={t("allergies.allergyLabel")}
              type="text"
              value={customAllergy}
              onChange={(e) => setCustomAllergy(e.target.value)}
              autoFocus
              size="md"
              variant="Underlined"
              showLabel={true}
            />
          </div>
        </FooterDrawer>
      )}
    </div>
  );
};

export default AddAllergy;
